import React, { useState } from "react";
import Icon from "components/AppIcon";
import Input from "components/ui/Input";
import Button from "components/ui/Button";

const DEFAULT_MILESTONES = [
  { id: "m1", label: "First Close", date: "2026-06-30", description: "Minimum 40% of target capital committed", status: "pending", critical: true },
  { id: "m2", label: "Final Close", date: "2026-09-30", description: "100% of target capital committed", status: "pending", critical: true },
  { id: "m3", label: "Capital Call 1", date: "2026-10-15", description: "Initial drawdown of 25% committed capital", status: "pending", critical: false },
  { id: "m4", label: "Asset Acquisition", date: "2026-12-01", description: "Completion of target asset purchase", status: "pending", critical: true },
  { id: "m5", label: "First Distribution", date: "2027-06-30", description: "Initial income distribution to LPs", status: "pending", critical: false },
];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending", color: "var(--color-muted-foreground)" },
  { value: "in-progress", label: "In Progress", color: "var(--color-warning)" },
  { value: "completed", label: "Completed", color: "var(--color-success)" },
  { value: "delayed", label: "Delayed", color: "var(--color-error)" },
];

function MilestoneItem({ milestone, onUpdate, onRemove, index }) {
  const [isEditing, setIsEditing] = useState(false);
  const statusConfig = STATUS_OPTIONS?.find((s) => s?.value === milestone?.status) || STATUS_OPTIONS?.[0];

  return (
    <div
      className="relative flex gap-4"
    >
      {/* Timeline line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center border-2 z-10 flex-shrink-0"
          style={{
            borderColor: milestone?.critical ? "var(--color-primary)" : "var(--color-border)",
            background: milestone?.status === "completed" ? "var(--color-success)" : "var(--color-card)",
          }}
        >
          {milestone?.status === "completed" ? (
            <Icon name="Check" size={14} color="white" strokeWidth={2.5} />
          ) : (
            <span className="font-data text-xs font-semibold" style={{ color: "var(--color-primary)" }}>
              {index + 1}
            </span>
          )}
        </div>
        <div className="w-0.5 flex-1 mt-1" style={{ background: "var(--color-border)", minHeight: "24px" }} />
      </div>
      {/* Content */}
      <div className="flex-1 pb-6 min-w-0">
        {isEditing ? (
          <div className="p-4 rounded-lg border" style={{ borderColor: "var(--color-primary)", background: "rgba(59,76,76,0.03)" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <Input
                label="Milestone Label"
                type="text"
                value={milestone?.label}
                onChange={(e) => onUpdate({ ...milestone, label: e?.target?.value })}
              />
              <Input
                label="Target Date"
                type="date"
                value={milestone?.date}
                onChange={(e) => onUpdate({ ...milestone, date: e?.target?.value })}
              />
              <Input
                label="Description"
                type="text"
                value={milestone?.description}
                onChange={(e) => onUpdate({ ...milestone, description: e?.target?.value })}
              />
              <div>
                <label className="block font-caption text-sm font-medium text-foreground mb-1.5">Status</label>
                <select
                  value={milestone?.status}
                  onChange={(e) => onUpdate({ ...milestone, status: e?.target?.value })}
                  className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  style={{ borderColor: "var(--color-border)" }}
                >
                  {STATUS_OPTIONS?.map((s) => <option key={s?.value} value={s?.value}>{s?.label}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="default" size="sm" onClick={() => setIsEditing(false)}>Save</Button>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={milestone?.critical}
                  onChange={(e) => onUpdate({ ...milestone, critical: e?.target?.checked })}
                  className="w-4 h-4 rounded"
                />
                <span className="font-caption text-sm text-foreground">Critical milestone</span>
              </label>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-3 group">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-caption font-semibold text-sm text-foreground">{milestone?.label}</span>
                {milestone?.critical && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-caption font-medium"
                    style={{ background: "rgba(59,76,76,0.1)", color: "var(--color-primary)" }}>
                    <Icon name="Flag" size={10} />Critical
                  </span>
                )}
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-caption font-medium"
                  style={{ background: `${statusConfig?.color}18`, color: statusConfig?.color }}
                >
                  {statusConfig?.label}
                </span>
              </div>
              <p className="font-caption text-xs text-muted-foreground mt-0.5">{milestone?.description}</p>
              <p className="font-data text-xs mt-1" style={{ color: "var(--color-primary)" }}>
                {milestone?.date ? new Date(milestone.date)?.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
              </p>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
              <button
                onClick={() => setIsEditing(true)}
                className="p-1.5 rounded hover:bg-muted transition-colors"
                title="Edit milestone"
              >
                <Icon name="Pencil" size={14} color="var(--color-muted-foreground)" />
              </button>
              <button
                onClick={() => onRemove(milestone?.id)}
                className="p-1.5 rounded hover:bg-muted transition-colors"
                title="Remove milestone"
              >
                <Icon name="Trash2" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function StepTimeline({ data, onChange, errors }) {
  const milestones = data?.milestones || DEFAULT_MILESTONES;

  const handleUpdate = (updated) => {
    onChange({ ...data, milestones: milestones?.map((m) => m?.id === updated?.id ? updated : m) });
  };

  const handleRemove = (id) => {
    onChange({ ...data, milestones: milestones?.filter((m) => m?.id !== id) });
  };

  const handleAdd = () => {
    const newM = {
      id: `m-${Date.now()}`,
      label: "New Milestone",
      date: "",
      description: "",
      status: "pending",
      critical: false,
    };
    onChange({ ...data, milestones: [...milestones, newM] });
  };

  return (
    <div className="space-y-6 lg:space-y-8">
      <div>
        <h3 className="font-heading text-lg lg:text-xl font-semibold text-foreground mb-1">
          Closing Timeline &amp; Milestones
        </h3>
        <p className="font-caption text-sm text-muted-foreground">
          Establish key dates, closing schedule, and critical milestones for the SPV lifecycle.
        </p>
      </div>
      {/* Key Dates */}
      <div className="p-4 lg:p-6 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Calendar" size={18} color="var(--color-primary)" />
          <h4 className="font-caption font-semibold text-foreground">Key Dates</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Marketing Launch Date"
            type="date"
            value={data?.marketingDate || ""}
            onChange={(e) => onChange({ ...data, marketingDate: e?.target?.value })}
            description="Date investor marketing materials are distributed."
          />
          <Input
            label="First Close Date"
            type="date"
            value={data?.firstCloseDate || ""}
            onChange={(e) => onChange({ ...data, firstCloseDate: e?.target?.value })}
            required
            error={errors?.firstCloseDate}
            description="Minimum capital threshold closing date."
          />
          <Input
            label="Final Close Date"
            type="date"
            value={data?.finalCloseDate || ""}
            onChange={(e) => onChange({ ...data, finalCloseDate: e?.target?.value })}
            required
            error={errors?.finalCloseDate}
            description="Hard deadline for all investor commitments."
          />
          <Input
            label="Investment Period End"
            type="date"
            value={data?.investmentPeriodEnd || ""}
            onChange={(e) => onChange({ ...data, investmentPeriodEnd: e?.target?.value })}
            description="Last date for making new investments."
          />
          <Input
            label="Fund Term (Years)"
            type="number"
            placeholder="e.g. 10"
            value={data?.fundTerm || ""}
            onChange={(e) => onChange({ ...data, fundTerm: e?.target?.value })}
            description="Total duration of the fund from final close."
          />
          <Input
            label="Extension Options (Years)"
            type="number"
            placeholder="e.g. 2"
            value={data?.extensionOptions || ""}
            onChange={(e) => onChange({ ...data, extensionOptions: e?.target?.value })}
            description="Number of one-year extension options available."
          />
        </div>
      </div>
      {/* Milestones Timeline */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="GitBranch" size={18} color="var(--color-primary)" />
            <h4 className="font-caption font-semibold text-foreground">
              Milestones ({milestones?.length})
            </h4>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            iconSize={14}
            onClick={handleAdd}
          >
            Add Milestone
          </Button>
        </div>

        <div className="pl-2">
          {milestones?.map((m, idx) => (
            <MilestoneItem
              key={m?.id}
              milestone={m}
              index={idx}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
            />
          ))}
        </div>
      </div>
      {/* Capital Call Schedule */}
      <div className="p-4 lg:p-6 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="ArrowDownToLine" size={18} color="var(--color-primary)" />
          <h4 className="font-caption font-semibold text-foreground">Capital Call Schedule</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block font-caption text-sm font-medium text-foreground mb-1.5">
              Call Frequency
            </label>
            <select
              value={data?.callFrequency || "as-needed"}
              onChange={(e) => onChange({ ...data, callFrequency: e?.target?.value })}
              className="w-full h-10 px-3 rounded-md border text-sm font-caption bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              style={{ borderColor: "var(--color-border)" }}
            >
              <option value="as-needed">As Needed</option>
              <option value="quarterly">Quarterly</option>
              <option value="semi-annual">Semi-Annual</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <Input
            label="Notice Period (Days)"
            type="number"
            placeholder="e.g. 10"
            value={data?.callNoticePeriod || ""}
            onChange={(e) => onChange({ ...data, callNoticePeriod: e?.target?.value })}
            description="Business days notice before capital call."
          />
          <Input
            label="Maximum Single Call (%)"
            type="number"
            placeholder="e.g. 25"
            value={data?.maxSingleCall || ""}
            onChange={(e) => onChange({ ...data, maxSingleCall: e?.target?.value })}
            description="Maximum % of commitment per single call."
          />
        </div>
      </div>
    </div>
  );
}